import { supabase } from '../lib/supabase';
import storageService from './storage';

const dossierService = {
  /**
   * Get all documents for a property
   */
  async getDocuments(propertyId, userId, filters = {}) {
    try {
      if (!propertyId || !userId) {
        throw new Error('Property ID and User ID are required');
      }

      let query = supabase
        .from('property_documents')
        .select('*')
        .eq('property_id', propertyId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      // Pagination
      const page = filters.page || 1;
      const limit = filters.limit || 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      // Get signed URLs for all documents
      const documentsWithUrls = await Promise.all(
        (data || []).map(async (doc) => {
          const urlResult = await storageService.getSignedUrl(doc.file_path);
          return {
            ...doc,
            url: urlResult?.url || null
          };
        })
      );

      return { success: true, data: documentsWithUrls, count: count || 0 };
    } catch (error) {
      console.error('Error fetching documents:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement des documents' };
    }
  },

  /**
   * Upload a document
   */
  async uploadDocument(propertyId, userId, file, metadata) {
    try {
      if (!propertyId || !userId || !file) {
        throw new Error('Property ID, User ID, and file are required');
      }

      // Upload file to storage
      const uploadResult = await storageService.uploadImage(file, userId, 'documents', true);
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Erreur lors de l\'upload');
      }

      // Create document record
      const { data, error } = await supabase
        .from('property_documents')
        .insert({
          property_id: propertyId,
          user_id: userId,
          type: metadata.type,
          title: metadata.title || file.name,
          description: metadata.description || null,
          file_path: uploadResult.filePath,
          mime_type: file.type,
          file_size: file.size,
          tags: metadata.tags || []
        })
        .select()
        .single();

      if (error) throw error;

      // Get signed URL
      const urlResult = await storageService.getSignedUrl(data.file_path);

      return {
        success: true,
        data: {
          ...data,
          url: urlResult?.url || null
        }
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: error?.message || 'Erreur lors de l\'upload du document' };
    }
  },

  /**
   * Update document metadata
   */
  async updateDocument(documentId, userId, updates) {
    try {
      const { data, error } = await supabase
        .from('property_documents')
        .update({
          title: updates.title,
          description: updates.description,
          type: updates.type,
          tags: updates.tags,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating document:', error);
      return { success: false, error: error?.message || 'Erreur lors de la mise à jour' };
    }
  },

  /**
   * Delete document (soft delete)
   */
  async deleteDocument(documentId, userId) {
    try {
      // Get document to delete file from storage
      const { data: doc } = await supabase
        .from('property_documents')
        .select('file_path')
        .eq('id', documentId)
        .eq('user_id', userId)
        .single();

      // Soft delete
      const { error } = await supabase
        .from('property_documents')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', documentId)
        .eq('user_id', userId);

      if (error) throw error;

      // Optionally delete from storage (or keep for recovery)
      // await storageService.deleteImage(doc?.file_path);

      return { success: true };
    } catch (error) {
      console.error('Error deleting document:', error);
      return { success: false, error: error?.message || 'Erreur lors de la suppression' };
    }
  },

  /**
   * Get all milestones for a property
   */
  async getMilestones(propertyId, userId, filters = {}) {
    try {
      if (!propertyId || !userId) {
        throw new Error('Property ID and User ID are required');
      }

      let query = supabase
        .from('property_milestones')
        .select('*')
        .eq('property_id', propertyId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('due_date', { ascending: true });

      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching milestones:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement des échéances' };
    }
  },

  /**
   * Create a milestone
   */
  async createMilestone(propertyId, userId, milestoneData) {
    try {
      const { data, error } = await supabase
        .from('property_milestones')
        .insert({
          property_id: propertyId,
          user_id: userId,
          category: milestoneData.category,
          name: milestoneData.name,
          due_date: milestoneData.due_date,
          status: milestoneData.status || 'UPCOMING',
          amount: milestoneData.amount || null,
          meta: milestoneData.meta || {},
          reminder_days: milestoneData.reminder_days || [365, 180, 90, 30]
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error creating milestone:', error);
      return { success: false, error: error?.message || 'Erreur lors de la création de l\'échéance' };
    }
  },

  /**
   * Update a milestone
   */
  async updateMilestone(milestoneId, userId, updates) {
    try {
      const { data, error } = await supabase
        .from('property_milestones')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', milestoneId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating milestone:', error);
      return { success: false, error: error?.message || 'Erreur lors de la mise à jour' };
    }
  },

  /**
   * Mark milestone as done
   */
  async markMilestoneDone(milestoneId, userId) {
    try {
      return await this.updateMilestone(milestoneId, userId, { status: 'DONE' });
    } catch (error) {
      console.error('Error marking milestone done:', error);
      return { success: false, error: error?.message || 'Erreur lors de la mise à jour' };
    }
  },

  /**
   * Delete milestone (soft delete)
   * Uses PostgreSQL function to bypass RLS issues
   */
  async deleteMilestone(milestoneId, userId) {
    try {
      // Try using the PostgreSQL function first (if it exists)
      const { data: functionResult, error: functionError } = await supabase
        .rpc('soft_delete_milestone', {
          milestone_id: milestoneId,
          user_id_param: userId
        });

      // If function exists and succeeds, return success
      if (!functionError && functionResult === true) {
        return { success: true };
      }

      // If function doesn't exist or fails, fall back to direct UPDATE
      if (functionError && functionError.code !== '42883') { // 42883 = function does not exist
        console.warn('Function call failed, falling back to direct UPDATE:', functionError);
      }

      // Fallback: Direct UPDATE (should work with the simplified RLS policy)
      const { error } = await supabase
        .from('property_milestones')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', milestoneId)
        .eq('user_id', userId)
        .is('deleted_at', null);

      if (error) {
        console.error('RLS Error details:', error);
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting milestone:', error);
      return { success: false, error: error?.message || 'Erreur lors de la suppression' };
    }
  },

  /**
   * Get notes for a property
   */
  async getNotes(propertyId, userId) {
    try {
      const { data, error } = await supabase
        .from('property_notes')
        .select('*')
        .eq('property_id', propertyId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching notes:', error);
      return { success: false, error: error?.message || 'Erreur lors du chargement des notes' };
    }
  },

  /**
   * Create or update a note
   */
  async saveNote(propertyId, userId, noteId, content) {
    try {
      if (noteId) {
        // Update existing note
        const { data, error } = await supabase
          .from('property_notes')
          .update({
            content,
            updated_at: new Date().toISOString()
          })
          .eq('id', noteId)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) throw error;
        return { success: true, data };
      } else {
        // Create new note
        const { data, error } = await supabase
          .from('property_notes')
          .insert({
            property_id: propertyId,
            user_id: userId,
            content
          })
          .select()
          .single();

        if (error) throw error;
        return { success: true, data };
      }
    } catch (error) {
      console.error('Error saving note:', error);
      return { success: false, error: error?.message || 'Erreur lors de la sauvegarde' };
    }
  },

  /**
   * Delete a note
   */
  async deleteNote(noteId, userId) {
    try {
      const { error } = await supabase
        .from('property_notes')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', noteId)
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting note:', error);
      return { success: false, error: error?.message || 'Erreur lors de la suppression' };
    }
  }
};

export default dossierService;
